import React, { useCallback, useContext, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { ScreenSize, UI, DropdownItem } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { TenantAccessRow } from "src/stores/TenantAccessStore";
import { useTranslation } from "react-i18next";
import styles from "./AccessFilter.module.css";

interface TenantAccessListProps {
    listIdentifier: string;
}

interface ListSelectionArgs {
    searchQuery?: string;
    filterItemValue?: string;
    sortItemValue?: string;
}

const desktopColumns = {
    realestateName: 0.2,
    realestateNumber: 0.13,
    objectName: 0.21,
    objectNumber: 0.14,
    leaseStart: 0.08,
    leaseEnd: 0.08,
    tenantName: 0.09,
    role: 0.07
};

const formatDate = (dateValue: string | null) => {
    if (!dateValue) {
        return "—";
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return dateValue;
    }

    return parsedDate.toLocaleDateString("de-CH");
};

const TenantAccessListBase = (props: TenantAccessListProps) => {
    const { t } = useTranslation();
    const { uiStore, navStore, userAccessStore, tenantAccessStore } = useContext(RootStoreContext);

    if (!navStore.listMap.has(props.listIdentifier)) {
        navStore.setListMapValue(props.listIdentifier, {});
    }

    const listParameters = navStore.getListMapValue(props.listIdentifier);

    let currentSearchQuery: string | undefined = undefined;
    if (listParameters && listParameters.searchQuery) {
        currentSearchQuery = listParameters.searchQuery;
    }

    let currentRoleFilter: string | undefined = undefined;
    if (listParameters && listParameters.sortValue) {
        currentRoleFilter = listParameters.sortValue;
    }

    const [searchQuery, setSearchQuery] = useState(currentSearchQuery ?? "");
    const [selectedRole, setSelectedRole] = useState(currentRoleFilter ?? "all");

    const persistFilters = useCallback(
        (nextSearchQuery: string | undefined, nextRoleFilter: string) => {
            navStore.setListMapValue(props.listIdentifier, {
                ...(listParameters ?? {}),
                searchQuery: nextSearchQuery,
                sortValue: nextRoleFilter,
                paginationPage: 1
            });
        },
        [listParameters, navStore, props.listIdentifier]
    );

    const roleFilterOptions = useMemo<DropdownItem[]>(() => {
        const roleIdsInRows = new Set<number>();
        tenantAccessStore.tenantAccessRows.forEach((row) => {
            tenantAccessStore.getRoleIds(row.role).forEach((roleId) => roleIdsInRows.add(roleId));
        });

        const roleItems = Array.from(roleIdsInRows)
            .map((roleId) => ({
                value: String(roleId),
                label: userAccessStore.roleLabelsById.get(roleId) ?? String(roleId)
            }))
            .sort((firstRole, secondRole) => firstRole.label.localeCompare(secondRole.label));

        return [{ value: "all", label: t("screens.dashboard.users.access.all_roles") }, ...roleItems];
    }, [tenantAccessStore, userAccessStore.roleLabelsById, t]);

    const selectedRoleItem = roleFilterOptions.find((option) => option.value === selectedRole) ?? roleFilterOptions[0];
    const effectiveSelectedRole = roleFilterOptions.some((option) => option.value === selectedRole) ? selectedRole : "all";

    const getRoleLabel = useCallback(
        (roleValue: TenantAccessRow["role"]) => {
            const roleIds = tenantAccessStore.getRoleIds(roleValue);
            return roleIds.length > 0
                ? roleIds.map((roleId) => userAccessStore.roleLabelsById.get(roleId) ?? String(roleId)).join(", ")
                : "—";
        },
        [tenantAccessStore, userAccessStore.roleLabelsById]
    );

    const renderDesktopRow = useCallback(
        (row: TenantAccessRow, rolesLabel: string) => (
            <UI.List.Row key={"r-1"}>
                <UI.List.Cell
                    key={"c-1"}
                    colspan={desktopColumns.realestateName}
                    value={row.realestatename ?? "—"}
                    className={styles.TenantWrapCell}
                />
                <UI.List.Cell
                    key={"c-2"}
                    colspan={desktopColumns.realestateNumber}
                    value={row.realestatenumber ?? "—"}
                    className={styles.TenantWrapCell}
                />
                <UI.List.Cell
                    key={"c-3"}
                    colspan={desktopColumns.objectName}
                    value={row.unitname ?? "—"}
                    className={styles.TenantWrapCell}
                />
                <UI.List.Cell
                    key={"c-4"}
                    colspan={desktopColumns.objectNumber}
                    value={row.unitnumber ?? "—"}
                    className={styles.TenantWrapCell}
                />
                <UI.List.Cell
                    key={"c-5"}
                    colspan={desktopColumns.leaseStart}
                    value={formatDate(row.leasestart)}
                    className={styles.TenantWrapCell}
                />
                <UI.List.Cell
                    key={"c-6"}
                    colspan={desktopColumns.leaseEnd}
                    value={formatDate(row.leaseend)}
                    className={styles.TenantWrapCell}
                />
                <UI.List.Cell
                    key={"c-7"}
                    colspan={desktopColumns.tenantName}
                    value={row.tenantname ?? "—"}
                    className={styles.TenantWrapCell}
                />
                <UI.List.Cell
                    key={"c-8"}
                    colspan={desktopColumns.role}
                    value={rolesLabel}
                    className={styles.TenantWrapCell}
                />
            </UI.List.Row>
        ),
        []
    );

    const renderMobileRow = useCallback(
        (row: TenantAccessRow, rolesLabel: string) => (
            <UI.List.Row key={"r-1"}>
                <UI.List.Cell key={"c-1"} colspan={0.65} value={`${row.realestatename ?? "—"} / ${row.unitname ?? "—"}`} />
                <UI.List.Cell key={"c-2"} colspan={0.35} value={rolesLabel} />
            </UI.List.Row>
        ),
        []
    );

    const renderItem = useCallback(
        (row: TenantAccessRow, screenSize: ScreenSize) => {
            const rolesLabel = getRoleLabel(row.role);

            const listRows =
                screenSize === ScreenSize.DESKTOP ? [renderDesktopRow(row, rolesLabel)] : [renderMobileRow(row, rolesLabel)];

            const itemKey = row.tenancyid ?? `${row.tenantid}-${row.unitid ?? "unit"}`;
            return <UI.List.Item key={itemKey} screenSize={screenSize} rows={listRows} />;
        },
        [getRoleLabel, renderDesktopRow, renderMobileRow]
    );

    const renderHeader = useCallback((screenSize: ScreenSize) => {
        let headerRows: React.ReactElement[] = [];

        switch (screenSize) {
            case ScreenSize.DESKTOP:
                headerRows = [
                    <UI.List.Row key={"row-1"}>
                        <UI.List.Cell
                            key={"c-1"}
                            colspan={desktopColumns.realestateName}
                            value={t("screens.dashboard.users.access.realestate_name")}
                        />
                        <UI.List.Cell
                            key={"c-2"}
                            colspan={desktopColumns.realestateNumber}
                            value={t("screens.dashboard.users.access.realestate_number")}
                        />
                        <UI.List.Cell key={"c-3"} colspan={desktopColumns.objectName} value={t("screens.dashboard.users.access.object_name")} />
                        <UI.List.Cell
                            key={"c-4"}
                            colspan={desktopColumns.objectNumber}
                            value={t("screens.dashboard.users.access.object_number")}
                        />
                        <UI.List.Cell key={"c-5"} colspan={desktopColumns.leaseStart} value={t("screens.dashboard.users.access.lease_start")} />
                        <UI.List.Cell key={"c-6"} colspan={desktopColumns.leaseEnd} value={t("screens.dashboard.users.access.lease_end")} />
                        <UI.List.Cell key={"c-7"} colspan={desktopColumns.tenantName} value={t("screens.dashboard.users.access.tenant_name")} />
                        <UI.List.Cell key={"c-8"} colspan={desktopColumns.role} value={t("screens.dashboard.users.access.role")} />
                    </UI.List.Row>
                ];
                break;
            case ScreenSize.MOBILE:
                headerRows = [
                    <UI.List.Row key={"row-1"}>
                        <UI.List.Cell key={"c-1"} colspan={0.65} value={t("labels.object")} />
                        <UI.List.Cell key={"c-2"} colspan={0.35} value={t("labels.roles")} />
                    </UI.List.Row>
                ];
                break;
        }

        return <UI.List.Header rows={headerRows} />;
    }, [t]);

    const onPageChanged = useCallback(
        (currentPage: number) => {
            navStore.setListMapValue(
                props.listIdentifier,
                listParameters
                    ? { ...listParameters, scrollPosition: window.pageYOffset, paginationPage: currentPage }
                    : { scrollPosition: window.pageYOffset, paginationPage: currentPage }
            );
        },
        [listParameters, navStore, props.listIdentifier]
    );

    const handleSearchInput = useCallback(
        ({ searchQuery: nextSearchQuery, sortItemValue }: ListSelectionArgs) => {
            const nextRole = sortItemValue ?? effectiveSelectedRole;
            setSearchQuery(nextSearchQuery ?? "");
            setSelectedRole(nextRole);
            persistFilters(nextSearchQuery, nextRole);
        },
        [persistFilters, effectiveSelectedRole]
    );

    const onChangeSort = useCallback(
        ({ searchQuery: nextSearchQuery, sortItemValue }: ListSelectionArgs) => {
            const nextRole = sortItemValue ?? "all";
            const resolvedSearchQuery = nextSearchQuery ?? searchQuery;
            setSelectedRole(nextRole);
            setSearchQuery(resolvedSearchQuery ?? "");
            persistFilters(resolvedSearchQuery, nextRole);
        },
        [persistFilters, searchQuery]
    );

    return (
        <UI.List.BasicList
            screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
            items={tenantAccessStore.filteredTenantAccessRows(searchQuery, effectiveSelectedRole)}
            renderItem={renderItem}
            renderHeader={renderHeader}
            scrollPosition={listParameters?.scrollPosition ?? undefined}
            itemsPerPage={12}
            initialPage={listParameters?.paginationPage ?? undefined}
            onPageChanged={onPageChanged}
            onSearch={handleSearchInput}
            initialSearchQuery={currentSearchQuery}
            sortItems={roleFilterOptions}
            initialSortItem={selectedRoleItem}
            onChangeSort={onChangeSort}
        />
    );
};

export const TenantAccessList = observer(TenantAccessListBase);
